import { Capacitor } from '@capacitor/core';
import { scanOverlay } from './scanOverlay.js';

/**
 * Eigene Pokémon-Bilder aus den Scans.
 *
 * Die Bilder entstehen beim Scannen, werden auf dem Gerät zugeschnitten und
 * bleiben dort. Der Server bekommt sie nie zu sehen — er nennt beim Scan nur
 * die Koordinaten des Ausschnitts.
 *
 * Damit zeigen die Karten das eigene Exemplar statt eines fremden Bildes.
 * Für Pokémon-Bilder gibt es keine Lizenz, die eigenen Aufnahmen gehören
 * dagegen dem Nutzer.
 *
 * Im Browser gibt es keine — dort bleiben die Karten bei ihren Platzhaltern.
 */

/**
 * Alle vorhandenen Bilder laden.
 *
 * Bewusst ohne Zwischenspeicher: Gescannt wird IM SPIEL, also während die
 * App im Hintergrund liegt. Wer zurückwechselt, erwartet sein neues Bild —
 * eine einmal gemerkte Liste hätte dafür einen Neustart verlangt.
 *
 * Der Aufruf liest ein Verzeichnis, das kostet nichts.
 *
 * @returns {Promise<Record<string, string>>} Kennung der Art → Bildadresse
 */
export async function ladeEigeneBilder() {
  const bilder = {};

  if (!Capacitor.isNativePlatform()) {
    return bilder;
  }

  try {
    const antwort = await scanOverlay.bilder();

    for (const [kennung, pfad] of Object.entries(antwort?.bilder ?? {})) {
      // Aus dem Dateipfad wird eine Adresse, die der eingebaute Browser
      // öffnen darf. Ohne diesen Schritt blockiert er den Zugriff.
      bilder[kennung] = Capacitor.convertFileSrc(pfad);
    }
  } catch {
    // Ohne Bilder ist die App vollständig benutzbar — kein Grund für eine
    // Fehlermeldung.
  }

  return bilder;
}

/**
 * Bilder laden und bei Rückkehr in die App erneut einlesen.
 *
 * @param {(bilder: Record<string, string>) => void} uebernehmen
 * @returns {() => void} Aufräumen
 */
export function beobachteEigeneBilder(uebernehmen) {
  let aktiv = true;

  const laden = () => {
    ladeEigeneBilder().then((b) => {
      if (aktiv) {
        uebernehmen(b);
      }
    });
  };

  laden();

  // Beim Zurückwechseln aus dem Spiel neu einlesen — das ist der Moment,
  // in dem ein frisch gescanntes Bild dazugekommen ist.
  const beiSichtbar = () => {
    if (document.visibilityState === 'visible') {
      laden();
    }
  };

  document.addEventListener('visibilitychange', beiSichtbar);

  return () => {
    aktiv = false;
    document.removeEventListener('visibilitychange', beiSichtbar);
  };
}
